/**
 * Client mirror of App\Services\Ontology\DataElementRules.
 *
 * The builder edits a graph that has not been saved yet, so it cannot ask the
 * server whether a data element is already claimed without a round trip per
 * keystroke. This is the same rule, transliterated, used only to drive the
 * authoring UI — the PHP remains the authority and revalidates on write.
 *
 * The guard against the two drifting is tests/fixtures/data-element-rules.json:
 * both this module and the PHP are tested against that one file, so a rule that
 * changes on one side only turns the other side red. If you change the logic
 * here, change DataElementRules.php in the same commit and add the case to the
 * fixture.
 */
import { TIERS } from './granularity';

/**
 * Characters, not UTF-16 code units — matching PHP's mb_strlen. Measuring
 * `.length` would reject a 128-character mapping written in an astral script
 * that the server happily accepts.
 */
export const MAX_LENGTH = 128;

/**
 * C0 controls (tab, newline and carriage return included — XML normalises those
 * inside an attribute value rather than carrying them), DEL, the C1 block, the
 * two noncharacters XML forbids, and lone surrogates, which are not Unicode
 * scalars and so cannot be encoded as UTF-8 at all.
 *
 * The surrogate half of this has no counterpart in the PHP character class: a
 * PHP string is bytes, so an unpaired surrogate fails its UTF-8 check instead.
 * Same rejection, reached differently, because the two languages represent the
 * broken value differently.
 */
// eslint-disable-next-line no-control-regex
const FORBIDDEN = /[\u0000-\u001F\u007F-\u009F\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

/** Only the whitespace XML itself would strip or fold. */
const TRIM = /^[ \t\n\r]+|[ \t\n\r]+$/g;

/**
 * A stored mapping: trimmed, with blank meaning absent.
 *
 * A non-string is returned untouched rather than nulled, so a malformed value
 * fails validation instead of vanishing.
 */
export function normaliseDataElementId(value) {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value !== 'string') {
        return value;
    }

    const trimmed = value.replace(TRIM, '');

    return trimmed === '' ? null : trimmed;
}

/**
 * Every rejected mapping in the graph, as `{ [depth]: { [propertyId]: reason } }`.
 *
 * One reason per property: the first thing wrong with it, value checked before
 * placement, so an author fixes the mapping itself before being told where it
 * may live.
 */
export function dataElementReasons(graph) {
    const reasons = {};
    const claims = {};

    const flag = (depth, property, reason) => {
        reasons[depth] = reasons[depth] ?? {};
        reasons[depth][property] = reason;
    };

    for (const [depth, node] of Object.entries(graph ?? {})) {
        const tier = TIERS.includes(node?.granularity) ? node.granularity : null;
        const properties = Array.isArray(node?.properties) ? node.properties : [];

        properties.forEach((property, index) => {
            if (!property || typeof property !== 'object' || !('data_element_id' in property)) {
                return;
            }

            const value = normaliseDataElementId(property.data_element_id);
            if (value === null) {
                return;
            }

            const key = String(property.id ?? index);

            if (typeof value !== 'string') {
                flag(depth, key, 'invalid_type');

                return;
            }

            if (FORBIDDEN.test(value)) {
                flag(depth, key, 'invalid_characters');

                return;
            }

            if ([...value].length > MAX_LENGTH) {
                flag(depth, key, 'too_long');

                return;
            }

            if (tier === null) {
                flag(depth, key, 'unreported_depth');

                return;
            }

            claims[tier] = claims[tier] ?? {};
            claims[tier][value] = claims[tier][value] ?? [];
            claims[tier][value].push([depth, key]);
        });
    }

    // Only mappings that got this far compete: a malformed one is already
    // reported for what is wrong with it, and counting it as a rival would
    // blame a well-formed property for its neighbour's typo.
    for (const byValue of Object.values(claims)) {
        for (const claimants of Object.values(byValue)) {
            if (claimants.length < 2) {
                continue;
            }

            for (const [depth, key] of claimants) {
                flag(depth, key, 'duplicate');
            }
        }
    }

    return reasons;
}

/** The reason one property's mapping is rejected, or null when it is fine. */
export function reasonForProperty(graph, depth, propertyId) {
    return dataElementReasons(graph)[depth]?.[String(propertyId)] ?? null;
}

const REASON_TEXT = {
    invalid_type: 'This data element id is not text.',
    invalid_characters:
        'Contains characters that cannot survive XML and JSON encoding. Angle brackets, ampersands and quotes '
        + 'are fine; tabs, newlines and other control characters are not.',
    too_long: `Longer than the ${MAX_LENGTH}-character limit.`,
    unreported_depth:
        'This depth has no reporting granularity, so nothing on it reaches the registry. Give the depth a tier, '
        + 'or clear the mapping.',
    duplicate: 'Another property in this tier already reports this data element.',
};

/** Terse wording for the input's error line; the API returns fuller prose. */
export function reasonText(reason) {
    return REASON_TEXT[reason] ?? null;
}

/**
 * Every data element already claimed in `tier`, so the builder can warn before
 * the author finishes typing a value that is going to collide.
 */
export function claimedInTier(graph, tier, { excludeDepth = null, excludeProperty = null } = {}) {
    const claimed = new Set();

    for (const [depth, node] of Object.entries(graph ?? {})) {
        if (node?.granularity !== tier) {
            continue;
        }

        const properties = Array.isArray(node?.properties) ? node.properties : [];

        properties.forEach((property, index) => {
            const key = String(property?.id ?? index);
            if (depth === excludeDepth && key === String(excludeProperty)) {
                return;
            }

            const value = normaliseDataElementId(property?.data_element_id);
            if (typeof value === 'string') {
                claimed.add(value);
            }
        });
    }

    return claimed;
}
